import axios from 'axios';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { isNative, platform } from './env';
import { storageGet, storageSet } from './storage';

// Native push notifications via Firebase Cloud Messaging.
//   iOS:     FCM token (FCM forwards to APNs using the .p8 uploaded to Firebase)
//   Android: FCM token directly
// One backend dispatch path for both platforms.
//
// Backend contract:
//   POST /Account/registerDeviceToken
//     { token, platform: 'ios'|'android', locale, timezone, appVersion }

const LAST_REGISTERED_TOKEN_KEY = 'push:lastRegisteredToken';

// spec 000211 (Q3): the backend contract above does not exist on dev yet (the server side lives on the
// stale EVC-1346 branch and needs its own spec). Until it ships, push stays off unless a build sets
// REACT_APP_PUSH_ENABLED=true - no permission prompt, no calls to endpoints that would 404.
const isPushEnabled = isNative && process.env.REACT_APP_PUSH_ENABLED === 'true';

// Request permission + fetch the FCM token. Returns null on permission denial
// or any failure (caller falls back silently).
export async function registerForPush() {
    if (!isPushEnabled) return null;
    try {
        let perm = await FirebaseMessaging.checkPermissions();
        if (perm.receive !== 'granted') {
            const req = await FirebaseMessaging.requestPermissions();
            perm = req;
        }
        if (perm.receive !== 'granted') return null;

        const result = await FirebaseMessaging.getToken();
        return result?.token || null;
    } catch {
        return null;
    }
}

// Bind delivery + tap handlers. onDeepLink is called with the path the
// notification wants to open (e.g. "/ChargePointStation/77/connector/1").
export async function bindPushHandlers({ onForegroundDelivery, onDeepLink, onTokenRefresh } = {}) {
    if (!isPushEnabled) return () => {};

    const subs = await Promise.all([
        FirebaseMessaging.addListener('notificationReceived', (event) => {
            onForegroundDelivery?.(event?.notification ?? event);
        }),
        FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
            const data = event?.notification?.data || {};
            const path = data.path || data.deepLinkPath;
            if (path && onDeepLink) onDeepLink(path);
        }),
        FirebaseMessaging.addListener('tokenReceived', (event) => {
            if (event?.token && onTokenRefresh) onTokenRefresh(event.token);
        }),
    ]);
    return () => {
        subs.forEach((s) => { try { s.remove?.(); } catch {} });
    };
}

// Tear down the push registration on logout: invalidate the FCM token locally
// (so the device stops being addressable) and tell the backend to drop its
// row. Backend endpoint is optional — silent failure is fine.
export async function unregisterPush() {
    if (!isPushEnabled) return;
    let token = null;
    try {
        const result = await FirebaseMessaging.getToken();
        token = result?.token || null;
    } catch { /* no token */ }

    try {
        if (token) {
            await axios.post('/Account/unregisterDeviceToken', { token }).catch(() => {});
        }
    } finally {
        try { await FirebaseMessaging.deleteToken(); } catch { /* best-effort */ }
        await storageSet(LAST_REGISTERED_TOKEN_KEY, '').catch(() => {});
    }
}

// Best-effort register-with-backend. Skips re-post when token hasn't changed.
export async function sendTokenToBackend(token) {
    if (!isPushEnabled || !token) return false;
    try {
        const lastSent = await storageGet(LAST_REGISTERED_TOKEN_KEY);
        if (lastSent === token) return true;

        const locale = (typeof navigator !== 'undefined' && navigator.language) || 'he-IL';
        const timezone = (Intl?.DateTimeFormat?.().resolvedOptions?.()?.timeZone) || 'UTC';
        await axios.post('/Account/registerDeviceToken', {
            token,
            platform,
            locale,
            timezone,
            appVersion: process.env.REACT_APP_VERSION || '0.1.0',
        });
        await storageSet(LAST_REGISTERED_TOKEN_KEY, token);
        return true;
    } catch {
        return false;
    }
}
